// @ts-nocheck
import { checkboxSizes } from "@insite/mobius/Checkbox";
import RadioGroupContext from "@insite/mobius/RadioGroup/RadioGroupContext";
import Typography from "@insite/mobius/Typography";
import applyPropBuilder from "@insite/mobius/utilities/applyPropBuilder";
import combineTypographyProps from "@insite/mobius/utilities/combineTypographyProps";
import { HasDisablerContext, withDisabler } from "@insite/mobius/utilities/DisablerContext";
import FieldSetPresentationProps from "@insite/mobius/utilities/fieldSetProps";
import getColor from "@insite/mobius/utilities/getColor";
import getContrastColor from "@insite/mobius/utilities/getContrastColor";
import InjectableCss, { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import MobiusStyledComponentProps from "@insite/mobius/utilities/MobiusStyledComponentProps";
import omitMultiple from "@insite/mobius/utilities/omitMultiple";
import resolveColor from "@insite/mobius/utilities/resolveColor";
import * as React from "react";
import styled, { withTheme } from "styled-components";

/* Store Custom Code - Start */
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
/* Store Custom Code - End */

/* Starter Custom Code - Start */
const getSizeVariant = (sizeVariant: keyof typeof checkboxSizes) => {
    return checkboxSizes[sizeVariant] || checkboxSizes["default"];
};
/* Starter Custom Code - End */

/* Store Custom Code - Start */
const getDotSize = (sizeVariant: keyof typeof checkboxSizes) => {
    return Math.ceil(getSizeVariant(sizeVariant).iconSize / 2 + 1);
};
/* Store Custom Code - End */

export interface RadioPresentationProps {
    /** CSS string or styled-components function to be injected into this component.
     * @themable */
    css?: StyledProp<RadioProps>;
    /**
     * Indicates how the `css` property is combined with the variant `css` property from the theme.
     * If true, the variant css is applied first and then the component css is applied after causing
     * a merge, much like normal CSS. If false, only the component css is applied, overriding the variant css in the theme.
     */
    mergeCss?: boolean;
}

export type RadioComponentProps = MobiusStyledComponentProps<
    "div",
    {
        /** Disables the radio button. */
        disabled?: boolean;
        /** Sets the value, if different from the radio button's `children`. */
        value?: string;
    }
>;

export type RadioProps = RadioComponentProps & FieldSetPresentationProps<RadioComponentProps> & RadioPresentationProps;

/* Starter Custom Code - Start */
export type RadioStyleProps = {
    _sizeVariant: keyof typeof checkboxSizes;
    _color: string;
};
/* Starter Custom Code - End */

/* Starter Custom Code - next line - RadioStyleProps */
const RadioStyle = styled.div<RadioStyleProps & InjectableCss>`
    display: flex; /* Starter Custom Code */
    input {
        opacity: 0;

        /* Starter Custom Code - Start */
        position: absolute;
        clip: rect(0, 0, 0, 0);

        /* Starter Custom Code - End */
        & + label {
            /* Starter Removed - margin-left */
            position: relative;
            display: inline-flex; /* Store Custom Code */
            align-items: center;
            &::before {
                content: "";
                box-sizing: border-box;
                display: inline-block;
                height: ${({ _sizeVariant }) => getSizeVariant(_sizeVariant).iconSize /* Starter Custom Code */}px;
                width: ${({ _sizeVariant }) => getSizeVariant(_sizeVariant).iconSize /* Starter Custom Code */}px;
                border-radius: 50%;
                border: ${pxToRem(1)} solid ${getColor("common.border")}; /* Store Custom Code */
                margin-right: ${pxToRem(10)}; /* Store Custom Code */

                /* Starter Custom Code */
                flex-shrink: 0;
                align-self: flex-start;

                /* Starter Custom Code */
            }

            /* Starter Custom Code */
            &::before,
            &::after {
                margin-top: ${pxToRem(3)}; /* Store Custom Code */
            }
            vertical-align: top;

            /* Starter Custom Code */
        }
        &:disabled + label {
            cursor: not-allowed;
        }
        &:focus + label::before {
            box-shadow: 0 0 0 ${pxToRem(2)} ${getColor("common.backgroundContrast")};
        }
        &:checked:not(:disabled) + label::before {
            border-color: ${({ _color, theme }) => resolveColor(_color, theme)};
            border-width: ${pxToRem(2)};
        }
        &:checked + label::after {
            content: "";
            display: block;
            height: ${({ _sizeVariant }) => getDotSize(_sizeVariant) /* Starter Custom Code */}px;
            width: ${({ _sizeVariant }) => getDotSize(_sizeVariant) /* Starter Custom Code */}px;
            border-radius: 50%;
            background: ${({ _color, theme }) => resolveColor(_color, theme)};
            position: absolute;

            /* Starter Custom Code */
            ${({ _sizeVariant }) => {
                const offset = (getSizeVariant(_sizeVariant).iconSize - getDotSize(_sizeVariant)) / 2;

                return `
                    left: ${offset}px;
                    top: ${offset}px;
                `;
            }}/* Starter Custom Code */
        }

        /* Starter Custom Code  */
        &:disabled + label::after {
            background: ${({ theme }) => resolveColor("common.disabled", theme)};
        }

        /* Starter Custom Code  */
    }
    ${injectCss}
`;

const Radio: React.FC<RadioProps & HasDisablerContext> = props => {
    const { children, disable, disabled, mergeCss, value, ...otherProps } = props;

    return (
        <RadioGroupContext.Consumer>
            {({ name, onChange, sizeVariant: sizeVariantFromContext, value: radioGroupValue }) => {
                const { applyProp, spreadProps, applyStyledProp } = applyPropBuilder(
                    { sizeVariant: sizeVariantFromContext, ...props },
                    { component: "radio", category: "fieldSet" },
                );
                // Because disabled html attribute doesn't accept undefined
                // eslint-disable-next-line no-unneeded-ternary
                const isDisabled = disable || disabled ? true : false;
                const sizeVariant = applyProp("sizeVariant", "default") as keyof typeof checkboxSizes;
                const typographyProps = combineTypographyProps({
                    theme: otherProps.theme!,
                    passedProps: spreadProps("typographyProps"),
                    defaultProps: {
                        size: checkboxSizes[sizeVariant].fontSize,
                    },
                });

                const radioValue = (value || (children as string)) ?? "";
                const id = `${name}-${radioValue.replace(/\W/g, "-")}`;
                const resolvedMergeCss = mergeCss ?? props.theme?.radio?.defaultProps?.mergeCss;
                return (
                    <RadioStyle
                        _color={applyProp("color", "primary")}
                        _sizeVariant={sizeVariant}
                        css={applyStyledProp("css", resolvedMergeCss)}
                    >
                        <input
                            type="radio"
                            id={id}
                            name={name}
                            value={radioValue}
                            checked={radioValue === radioGroupValue}
                            {...{ disabled: isDisabled, onChange }}
                            {...omitMultiple(otherProps, ["typographyProps", "color", "css"])}
                        />
                        <Typography
                            {...typographyProps}
                            color={isDisabled ? "text.disabled" : typographyProps.color}
                            as="label"
                            htmlFor={id}
                        >
                            {children}
                        </Typography>
                    </RadioStyle>
                );
            }}
        </RadioGroupContext.Consumer>
    );
};

Radio.displayName = "Radio";
/** @component */
export default withDisabler(withTheme(Radio));

export { RadioStyle };
